import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import gradeService from "../services/gradeService";
import classService from "../services/classService";
import enrollmentService from "../services/enrollmentService";
import { getCurrentSemester } from "../utils/dateUtils";
import { getGradeColor } from "../utils/gradeColors";
import LoadingSpinner from "./common/LoadingSpinner";
import LearnerSemesterReviewWidget from "./semesterreview/LearnerSemesterReviewWidget";
import { cache, cacheKeys } from "../utils/cacheUtils";
import DashboardCard from "./common/DashboardCard";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { calculateOverallAverage } from "../utils/gradeCalculations";

function LearnerDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overallAverage: 0,
    schoolSemesters: [],
    totalGrades: 0,
    completedSubjects: 0,
    trend: "stable",
    gradeDistribution: [],
    semesterAverages: [],
    recentGrades: [],
    subjectPerformance: [],
    attendanceRate: 0,
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Try to get cached data first
      const cachedGrades = cache.get(cacheKeys.grades(user.id));
      const cachedClasses = cache.get(`learner-classes-${user.id}`);
      const cachedSubjects = cache.get(cacheKeys.allSubjects());

      let allGrades, myClassesResponse, subjects;

      if (cachedGrades && cachedClasses && cachedSubjects) {
        allGrades = cachedGrades;
        myClassesResponse = cachedClasses;
        subjects = cachedSubjects;
      } else {
        // Fetch student's classes, grades, and subjects in parallel
        [allGrades, myClassesResponse, subjects] = await Promise.all([
          gradeService.getMyGrades(),
          classService.getMyClasses().catch((error) => {
            console.error("Error loading my classes:", error);
            return { content: [] };
          }),
          enrollmentService.getSubjects().catch((error) => {
            console.error("Error loading subjects:", error);
            return [];
          }),
        ]);

        // Cache the data (3 min TTL)
        cache.set(cacheKeys.grades(user.id), allGrades, 3 * 60 * 1000);
        cache.set(
          `learner-classes-${user.id}`,
          myClassesResponse,
          3 * 60 * 1000
        );
        cache.set(cacheKeys.allSubjects(), subjects, 3 * 60 * 1000);
      }

      // Handle paginated response from backend
      const myClasses = myClassesResponse?.content || myClassesResponse || [];

      // Group classes by school and calculate current semester for each
      const today = new Date();
      // Keyed by class, not by school: a learner can attend two classes and
      // the card names the class they are in.
      const schoolSemesters = {};

      myClasses.forEach((classItem) => {
        const classId = classItem.id ?? classItem.classId;
        const className =
          classItem.name || classItem.className || `Class ${classId}`;

        // Calculate current semester using the same logic as SemesterTimeline
        const currentSemesterInfo = classItem.startDate
          ? getCurrentSemester(classItem.startDate, classItem.professionType, today)
          : null;

        const semesterName =
          currentSemesterInfo && currentSemesterInfo.isActive
            ? `Semester ${currentSemesterInfo.semesterNumber}`
            : null;

        if (!schoolSemesters[classId]) {
          schoolSemesters[classId] = {
            className: className,
            currentSemesterName: semesterName,
            classes: [],
          };
        } else if (
          semesterName &&
          !schoolSemesters[classId].currentSemesterName
        ) {
          // Update if we found an active semester and didn't have one yet
          schoolSemesters[classId].currentSemesterName = semesterName;
        }

        schoolSemesters[classId].classes.push(classItem);
      });

      // Create subject map for quick lookup
      const subjectMap = {};
      if (subjects && subjects.length > 0) {
        subjects.forEach((s) => {
          subjectMap[s.id] = s.name;
        });
      }

      // Enrich grades with subject information
      const enrichedGrades = allGrades.map((g) => ({
        ...g,
        subject: {
          id: g.subjectId,
          name: subjectMap[g.subjectId] || "Unknown Subject",
        },
      }));

      // Calculate statistics
      const stats = calculateStatistics(enrichedGrades, schoolSemesters);
      setDashboardData(stats);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      console.error("Error details:", error.response?.data || error.message);

      // Set empty data on error
      setDashboardData({
        overallAverage: 0,
        schoolSemesters: [],
        totalGrades: 0,
        completedSubjects: 0,
        trend: "stable",
        gradeDistribution: [],
        semesterAverages: [],
        recentGrades: [],
        subjectPerformance: [],
        attendanceRate: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  const calculateStatistics = (grades, schoolSemesters) => {
    if (!grades || grades.length === 0) {
      return {
        overallAverage: 0,
        schoolSemesters: [],
        totalGrades: 0,
        completedSubjects: 0,
        trend: "stable",
        gradeDistribution: [],
        semesterAverages: [],
        recentGrades: [],
        subjectPerformance: [],
        attendanceRate: 95,
      };
    }

    // Filter valid grades - check both 'grade' and 'gradeValue' fields
    const validGrades = grades.filter((g) => {
      const gradeVal = g.gradeValue || g.grade;
      const isValid =
        gradeVal &&
        !isNaN(parseFloat(gradeVal)) &&
        parseFloat(gradeVal) >= 1 &&
        parseFloat(gradeVal) <= 6;
      return isValid;
    });

    // Overall average - Use the standardized calculation (Average of Subject Averages)
    const overallAverage = calculateOverallAverage(validGrades) || 0;

    // Grade distribution for chart
    const gradeRanges = [
      { range: "5.5-6.0", min: 5.5, max: 6.0, count: 0, color: "#10B981" },
      { range: "5.0-5.4", min: 5.0, max: 5.4, count: 0, color: "#34D399" },
      { range: "4.5-4.9", min: 4.5, max: 4.9, count: 0, color: "#6EE7B7" },
      { range: "4.0-4.4", min: 4.0, max: 4.4, count: 0, color: "#FCD34D" },
      { range: "3.5-3.9", min: 3.5, max: 3.9, count: 0, color: "#FBBF24" },
      { range: "3.0-3.4", min: 3.0, max: 3.4, count: 0, color: "#F59E0B" },
      { range: "< 3.0", min: 0, max: 2.9, count: 0, color: "#EF4444" },
    ];

    validGrades.forEach((g) => {
      const grade = parseFloat(g.gradeValue || g.grade);
      const range = gradeRanges.find((r) => grade >= r.min && grade <= r.max);
      if (range) range.count++;
    });

    // Semester averages trend - group by month/year for simplicity
    const semesterStats = {};
    validGrades.forEach((g) => {
      const examDate = new Date(g.examDate);
      const year = examDate.getFullYear();
      const month = examDate.getMonth();
      // Group by semester periods (roughly 6 months each)
      const semesterPeriod = month < 6 ? `${year} S1` : `${year} S2`;

      if (!semesterStats[semesterPeriod]) {
        semesterStats[semesterPeriod] = {
          grades: [],
          semester: semesterPeriod,
          startDate: g.examDate,
        };
      }
      semesterStats[semesterPeriod].grades.push(
        parseFloat(g.gradeValue || g.grade)
      );
    });

    const semesterAverages = Object.values(semesterStats)
      .map((s) => ({
        semester: s.semester,
        average: (
          s.grades.reduce((a, b) => a + b, 0) / s.grades.length
        ).toFixed(2),
        count: s.grades.length,
        startDate: s.startDate,
      }))
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .slice(-6); // Last 6 periods

    // Recent grades (last 10)
    const recentGrades = [...validGrades]
      .sort(
        (a, b) =>
          new Date(b.examDate || b.createdAt || 0) -
          new Date(a.examDate || a.createdAt || 0)
      )
      .slice(0, 10)
      .map((g) => {
        const examDate = new Date(g.examDate);
        const year = examDate.getFullYear();
        const month = examDate.getMonth();
        const semesterPeriod = month < 6 ? `${year} S1` : `${year} S2`;

        return {
          subject: g.subject?.name || "Subject",
          grade: parseFloat(g.gradeValue || g.grade).toFixed(1),
          date: g.examDate
            ? new Date(g.examDate).toLocaleDateString("de-CH")
            : "N/A",
          semester: semesterPeriod,
        };
      });

    // Subject performance (top subjects by grade)
    const subjectStats = {};
    validGrades.forEach((g) => {
      const subject = g.subject?.name || "Unknown";
      if (subject && subject !== "Unknown") {
        if (!subjectStats[subject]) {
          subjectStats[subject] = { grades: [], subject };
        }
        subjectStats[subject].grades.push(parseFloat(g.gradeValue || g.grade));
      }
    });

    const subjectPerformance = Object.values(subjectStats)
      .map((s) => ({
        subject: s.subject,
        average: s.grades.reduce((a, b) => a + b, 0) / s.grades.length,
        count: s.grades.length,
      }))
      .filter((s) => s.count >= 1) // Only subjects with at least 1 grade
      .sort((a, b) => b.average - a.average)
      .slice(0, 8); // Top 8 subjects

    // Trend calculation
    let trend = "stable";
    if (semesterAverages.length >= 2) {
      const lastTwo = semesterAverages.slice(-2);
      const diff =
        parseFloat(lastTwo[1].average) - parseFloat(lastTwo[0].average);
      if (diff > 0.2) trend = "up";
      else if (diff < -0.2) trend = "down";
    }

    // Completed subjects (unique subjects with grades)
    const completedSubjects = new Set(
      validGrades
        .map((g) => g.subject?.name)
        .filter((name) => name && name !== "Unknown")
    ).size;

    // Convert the per-class map into display rows: "<class> - Semester N"
    const schoolSemestersArray = Object.values(schoolSemesters || {}).map(
      (entry) => ({
        displayText: entry.currentSemesterName
          ? `${entry.className} - ${entry.currentSemesterName}`
          : `${entry.className} - No active semester`,
      })
    );

    return {
      overallAverage: parseFloat(overallAverage.toFixed(2)),
      schoolSemesters: schoolSemestersArray,
      totalGrades: validGrades.length,
      completedSubjects,
      trend,
      gradeDistribution: gradeRanges.filter((r) => r.count > 0),
      semesterAverages,
      recentGrades,
      subjectPerformance,
      attendanceRate: 95, // Placeholder
    };
  };

  if (loading) {
    return <LoadingSpinner message="Loading your dashboard..." size="lg" />;
  }

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const isItOrMedi =
    user?.professionType === "INFORMATIK" ||
    user?.professionType === "INFORMATIKER" ||
    user?.professionType === "MEDIAMATIK" ||
    user?.professionType === "MEDIAMATIKER";

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk">
                  {getTimeOfDay()}, {user?.firstName}!
                </h1>
                <p className="text-slate-cloud mt-2">
                  Here's your learning journey overview.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Overall Average */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-sunrise-red">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Overall Average
                </p>
                <p className={`text-3xl font-bold text-grey-dusk mt-2`}>
                  {dashboardData.overallAverage > 0
                    ? dashboardData.overallAverage.toFixed(2)
                    : "—"}
                </p>
              </div>
              <div className="bg-sunrise-red/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-sunrise-red"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Current Semesters */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-green-cloud">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Current Semesters
                </p>
                <div className="mt-2 space-y-1">
                  {dashboardData.schoolSemesters.length > 0 ? (
                    dashboardData.schoolSemesters.map((school, index) => (
                      <p
                        key={index}
                        className="text-sm font-semibold text-grey-dusk"
                      >
                        {school.displayText}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-grey-dusk">—</p>
                  )}
                </div>
              </div>
              <div className="bg-green-cloud/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-green-cloud"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Grades */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-warm-sunset">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Total Grades
                </p>
                <p className="text-3xl font-bold text-grey-dusk mt-2">
                  {dashboardData.totalGrades}
                </p>
              </div>
              <div className="bg-warm-sunset/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-warm-sunset"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Subjects Completed */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-slate-cloud">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">Subjects</p>
                <p className="text-3xl font-bold text-grey-dusk mt-2">
                  {dashboardData.completedSubjects}
                </p>
              </div>
              <div className="bg-slate-cloud/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-slate-cloud"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Grade Distribution */}
          <div className="bg-ice-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-grey-dusk mb-4">
              Grade Distribution
            </h3>
            {dashboardData.gradeDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.gradeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="range"
                    tick={{ fill: "#6B7280", fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                    formatter={(value) => [`${value} grades`, "Count"]}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {dashboardData.gradeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                No grade distribution available yet
              </div>
            )}
          </div>

          {/* Recent Grades */}
          <div className="bg-ice-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-grey-dusk mb-4">
              Recent Grades
            </h3>
            {dashboardData.recentGrades.length > 0 ? (
              <div className="overflow-y-auto max-h-[300px] space-y-2">
                {dashboardData.recentGrades.map((grade, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border hover:shadow-md transition-shadow"
                    style={{ 
                      borderColor: getGradeColor(parseFloat(grade.grade)),
                      backgroundColor: `${getGradeColor(parseFloat(grade.grade))}20`
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-grey-dusk truncate">
                        {grade.subject}
                      </p>
                      <p className="text-xs text-gray-500">
                        {grade.semester} • {grade.date}
                      </p>
                    </div>
                    <div
                      className="text-2xl font-bold ml-4"
                      style={{ color: getGradeColor(parseFloat(grade.grade)) }}
                    >
                      {grade.grade}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                No recent grades available yet
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <DashboardCard
            to="/grades"
            icon={
              <svg
                className="w-5 h-5 text-sunrise-red"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            }
            iconBg="sunrise-red"
            iconColor="sunrise-red"
            hoverBorder="sunrise-red"
            title="My Grades"
            text="View and manage your grades"
            linkText="View Grades"
          />

          <DashboardCard
            to="/profile"
            icon={
              <svg
                className="w-5 h-5 text-green-cloud"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            }
            iconBg="green-cloud"
            iconColor="green-cloud"
            hoverBorder="green-cloud"
            title="My Profile"
            text="Manage your account settings"
            linkText="View Profile"
          />

          <DashboardCard
            to="/learning-documentation"
            icon="fas fa-book"
            iconBg="grey-dusk"
            iconColor="grey-dusk"
            hoverBorder="grey-dusk"
            title="Learning Docs"
            text="Track your learning progress"
            linkText="View Docs"
          />

          {isItOrMedi && (
            <DashboardCard
              to="/learner/semester-reviews"
              icon="fas fa-clipboard-check"
              iconBg="sunrise-red"
              iconColor="sunrise-red"
              hoverBorder="sunrise-red"
              title="Reviews"
              text="Your semester reviews"
              linkText="View Reviews"
            />
          )}
          {/* KV / FKD / DHF learners do not see the Reviews card */}
          {/* Uncomment to re-enable for all learners:
          <DashboardCard
            to="/learner/semester-reviews"
            icon="fas fa-clipboard-check"
            iconBg="sunrise-red"
            iconColor="sunrise-red"
            hoverBorder="sunrise-red"
            title="Reviews"
            text="Your semester reviews"
            linkText="View Reviews"
          />
          */}
        </div>

        {/* Semester Reviews Widget */}
        {isItOrMedi && (
          <div className="mb-8">
            <LearnerSemesterReviewWidget />
          </div>
        )}
        {/* KV / FKD / DHF learners do not see the Semester Reviews widget */}
        {/* Uncomment to re-enable for all learners:
        <div className="mb-8">
          <LearnerSemesterReviewWidget />
        </div>
        */}
      </div>
    </div>
  );
}

export default LearnerDashboard;
